// Pictures pasted into a knowledge answer are stored server-side and referenced from the answer's
// Markdown as `/catalog-images/{guid}`. The browser cannot load that path directly - the picture
// is behind the API's bearer token - so everything that shows one goes through a resolver that
// fetches it with the token and hands back an object URL.

export const CATALOG_IMAGE_PREFIX = '/catalog-images/';
export const MAX_CATALOG_IMAGE_BYTES = 500 * 1024;
export const CATALOG_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif'];

const GUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const MARKER = new RegExp(`^${CATALOG_IMAGE_PREFIX}(${GUID})$`);

export const catalogImageId = (src?: string | null): string | null => {
  const match = MARKER.exec((src ?? '').trim());
  return match ? match[1].toLowerCase() : null;
};

export const catalogImageMarker = (id: string) => `${CATALOG_IMAGE_PREFIX}${id}`;

export const isLocalImageSrc = (src?: string | null) => /^(data|blob):/i.test(src ?? '');

/**
 * What the editor writes into the answer for an image. The marker when the picture was uploaded;
 * an ordinary web address as it is; and never a `data:` or `blob:` URL - a blob URL exists only in
 * this browser tab, and a data URI would put the picture's bytes into the answer text itself.
 */
export const serializedImageSrc = (data: { src?: string; marker?: string }): string | null =>
  data.marker ?? (!data.src || isLocalImageSrc(data.src) ? null : data.src);

export type ImageAlignment = 'left' | 'center' | 'right';
export const MIN_IMAGE_WIDTH_PERCENT = 10;
export const MIN_IMAGE_WIDTH_PX = 40;

export type ImageLayout = { alignment?: ImageAlignment; widthPercent?: number; widthPx?: number; naturalWidth?: number; naturalHeight?: number };

/** The picture's own size as the title carries it, `1600x900`. */
const SIZE_TOKEN = /^(\d{1,5})x(\d{1,5})$/;

/**
 * A picture's alignment and width ride in the Markdown image title: `![](src "left 400px")`. Either part may be
 * missing - answers saved before resizing existed carry `"left"` or no title at all. `none` is the editor
 * library's word for centred. Widths were a percentage of the editor before they were pixels (`"left 50%"`);
 * those still read, but a percentage of a wide editor and of a narrow Teams card are different sizes, which is
 * why new widths are pixels.
 */
export const parseImageTitle = (title?: string | null): ImageLayout => {
  const result: ImageLayout = {};
  for (const token of (title ?? '').trim().toLowerCase().split(/\s+/)) {
    if (token === 'left' || token === 'right' || token === 'center') result.alignment = token;
    else if (token === 'none') result.alignment = 'center';
    else {
      const percent = /^(\d{1,3})%$/.exec(token);
      const px = /^(\d{1,5})px$/.exec(token);
      const size = SIZE_TOKEN.exec(token);
      if (percent) result.widthPercent = clampWidthPercent(Number(percent[1]));
      else if (px && Number(px[1]) > 0) result.widthPx = clampWidthPx(Number(px[1]));
      else if (size && Number(size[1]) > 0 && Number(size[2]) > 0) {
        result.naturalWidth = Number(size[1]);
        result.naturalHeight = Number(size[2]);
      }
    }
  }
  return result;
};

export const clampWidthPercent = (percent: number) =>
  Math.round(Math.min(100, Math.max(MIN_IMAGE_WIDTH_PERCENT, percent)));

/** Never narrower than a usable picture, and - when it is known - never wider than the picture really is. */
export const clampWidthPx = (px: number, max?: number) =>
  Math.round(Math.max(MIN_IMAGE_WIDTH_PX, max ? Math.min(px, max) : px));

/** The CSS width a layout asks for, or undefined for a picture shown at its own size. */
export const imageWidthCss = (layout: ImageLayout): string | undefined => {
  if (layout.widthPx) return `${layout.widthPx}px`;
  if (layout.widthPercent && layout.widthPercent < 100) return `${layout.widthPercent}%`;
  return undefined;
};

/** The margins that place a block picture left, centred or right. */
export const alignmentMargins = (alignment: string): { marginLeft?: string; marginRight?: string } => {
  if (alignment === 'center') return { marginLeft: 'auto', marginRight: 'auto' };
  if (alignment === 'right') return { marginLeft: 'auto' };
  return {};
};

/**
 * The title to write. A picture at its own size has no width, so the Markdown stays as it was. `size` is the
 * picture's own pixel size (`1600x900`), so a page can hold its exact space while it loads. Teams and email
 * read the same title and skip a word they do not know, so it changes nothing there.
 */
export const formatImageTitle = (alignment?: string | null, width?: string | null, size?: string | null): string => {
  const parts: string[] = [];
  // Centred is written out, not left blank: a picture with no alignment loads as left, so leaving `none` out
  // turned every centred picture left again on the next open.
  if (alignment) parts.push(alignment === 'none' ? 'center' : alignment);
  const css = imageWidthCss(parseImageTitle(width));
  if (css) parts.push(css);
  const natural = parseImageTitle(size);
  if (natural.naturalWidth) parts.push(imageSizeToken(natural.naturalWidth, natural.naturalHeight));
  return parts.join(' ');
};

export const imageSizeToken = (width: number, height: number) => `${Math.round(width)}x${Math.round(height)}`;

/**
 * The Markdown with every picture's size word taken out of its title. The editor learns a picture's size only
 * once it has drawn it, so an answer saved before sizes existed gains them the moment it is opened - which is
 * not a change anyone made, and must not light up Save.
 */
export const withoutImageSizes = (markdown: string): string =>
  (markdown ?? '').replace(/!\[[^\]]*\]\([^)]*\)/g, picture =>
    picture
      .replace(/(["\s])\d{1,5}x\d{1,5}(?=["\s])/g, '$1')
      .replace(/" +/g, '"')
      .replace(/ +"\)$/, '")')
      .replace(/ ""\)$/, ')'));

export const parseDataUrl = (dataUrl: string): { type: string; bytes: Uint8Array } | null => {
  const match = /^data:([^;,]+)(;base64)?,([\s\S]*)$/i.exec(dataUrl ?? '');
  if (!match?.[2]) return null;
  try {
    const binary = atob(match[3]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return { type: match[1].toLowerCase(), bytes };
  } catch {
    return null;
  }
};

/** A cheap early refusal; the server decides by the file's signature regardless. */
export const catalogImageProblem = (type: string, size: number): 'type' | 'size' | null => {
  if (!CATALOG_IMAGE_TYPES.includes((type ?? '').toLowerCase())) return 'type';
  if (size > MAX_CATALOG_IMAGE_BYTES) return 'size';
  return null;
};

/** The widest a picture is made when it has to shrink. Teams cards and emails show it far narrower than this. */
export const MAX_SHRUNK_WIDTH = 1600;

export type ShrinkStep = { width: number; type: 'image/png' | 'image/jpeg'; quality?: number };

/**
 * The re-encodings to try, in order, for a picture over the cap; the first that fits wins. PNG first because it
 * keeps a screenshot's text sharp, and only when the width actually drops - redrawing a PNG at its own size
 * through a canvas rarely makes it smaller. Then JPEG, which is what brings a busy full-screen capture under.
 */
export const shrinkSteps = (width: number): ShrinkStep[] => {
  const widths = [MAX_SHRUNK_WIDTH, 1200, 900].map(w => Math.min(w, width)).filter((w, i, all) => all.indexOf(w) === i);
  const png: ShrinkStep[] = widths[0] < width ? [{ width: widths[0], type: 'image/png' }] : [];
  return [...png, ...widths.map(w => ({ width: w, type: 'image/jpeg' as const, quality: 0.85 }))];
};

/**
 * The picture to upload: unchanged when it already fits, otherwise shrunk in the browser until it does, so the
 * server never does this work. A GIF is never redrawn - a canvas keeps only its first frame - so one over the cap
 * is refused with its own reason ('gif-size'): the general message says the picture "could not be made smaller",
 * which for a GIF was never tried. Anything else still too big after every step is 'size'. The server checks type
 * and size again regardless.
 */
export const fitCatalogImage = async <T extends { size: number; type: string }>(
  file: T,
  measure: (file: T) => Promise<{ width: number; height: number }>,
  encode: (file: T, step: ShrinkStep, height: number) => Promise<T | null>
): Promise<T | 'type' | 'size' | 'gif-size'> => {
  if (catalogImageProblem(file.type, 0)) return 'type';
  if (file.size <= MAX_CATALOG_IMAGE_BYTES) return file;
  if (file.type.toLowerCase() === 'image/gif') return 'gif-size';

  const { width, height } = await measure(file);
  for (const step of shrinkSteps(width)) {
    const shrunk = await encode(file, step, Math.max(1, Math.round(height * step.width / width)));
    if (shrunk && shrunk.size <= MAX_CATALOG_IMAGE_BYTES) return shrunk;
  }
  return 'size';
};

/** How long the picture pop-up's Link tab waits for an address to load before calling it not a picture. */
export const LINK_CHECK_MS = 10000;

/**
 * Whether an address loads as a picture in this browser, given up on after `timeoutMs`. The Link tab asks before
 * inserting: an address that is not a picture - a web page pasted by mistake - was saved, drew as nothing anywhere,
 * and could not be found in the editor to take out again.
 */
export const loadsAsImage = (src: string, timeoutMs = LINK_CHECK_MS, makeImage: () => HTMLImageElement = () => new Image()): Promise<boolean> =>
  new Promise(resolve => {
    const img = makeImage();
    const timer = setTimeout(() => resolve(false), timeoutMs);
    img.onload = () => { clearTimeout(timer); resolve(img.naturalWidth > 0); };
    img.onerror = () => { clearTimeout(timer); resolve(false); };
    img.src = src;
  });

/** A picture that became a JPEG should not keep a `.png` name. */
export const fileNameForType = (fileName: string, type: string) =>
  type === 'image/jpeg' && /\.png$/i.test(fileName) ? fileName.replace(/\.png$/i, '.jpg') : fileName;

/**
 * Fetches each picture once per page. The API sends `no-store` on every response, so the browser
 * will not cache these - without this every card and every re-render would download again.
 * A failed fetch is forgotten so the next render can try once more.
 *
 * `ticketId` is for a picture in a comment: the server then lets anyone who can see the comment see the
 * picture, whatever its catalog item has become. It is part of the cache key, so a refusal without it can
 * never be what a ticket's activity is handed.
 */
export const createCatalogImageResolver = <T>(fetchPicture: (id: string, thumbnail: boolean, ticketId?: number) => Promise<T>, toUrl: (picture: T) => string) => {
  const cache = new Map<string, Promise<string>>();
  const key = (id: string, thumbnail: boolean, ticketId?: number) => {
    const ticket = ticketId ? `:ticket-${ticketId}` : '';
    return `${id.toLowerCase()}:${thumbnail ? 'thumb' : 'full'}${ticket}`;
  };

  return {
    resolve(id: string, thumbnail = false, ticketId?: number): Promise<string> {
      const k = key(id, thumbnail, ticketId);
      if (!cache.has(k)) {
        cache.set(k, fetchPicture(id, thumbnail, ticketId).then(toUrl).catch(e => {
          cache.delete(k);
          throw e;
        }));
      }
      return cache.get(k);
    },
    /** A picture this page just uploaded is already on hand - no need to fetch it back. */
    remember(id: string, url: string) {
      cache.set(key(id, false), Promise.resolve(url));
    }
  };
};

/** What the placeholder needs from CSS - spelt out so this module stays free of React. */
export type PlaceholderStyle = { display: 'block'; maxWidth: string; width: string; aspectRatio: string; marginLeft?: string; marginRight?: string };

/** For a picture whose size was never saved: a typical screenshot shape, at a width that reads as "a picture". */
const PLACEHOLDER_RATIO = '3 / 2';
const PLACEHOLDER_WIDTH = '320px';

/**
 * The space a picture will take, held while it loads so the text around it does not jump when it arrives. The
 * title carries the picture's own size (`1600x900`) once the editor has saved it, which gives the exact shape;
 * an answer saved before that gets a 3:2 box. Width as the picture will have it: the editor's width, else its
 * own size, never wider than the space.
 */
export const placeholderLayout = (title?: string): PlaceholderStyle => {
  const layout = parseImageTitle(title);
  const { alignment, naturalWidth, naturalHeight } = layout;
  return {
    display: 'block',
    maxWidth: '100%',
    width: imageWidthCss(layout) ?? (naturalWidth ? `${naturalWidth}px` : PLACEHOLDER_WIDTH),
    aspectRatio: naturalWidth && naturalHeight ? `${naturalWidth} / ${naturalHeight}` : PLACEHOLDER_RATIO,
    ...alignmentMargins(alignment)
  };
};

/** The message of a thrown Error, or empty for anything else thrown. */
export const errorMessage = (e: unknown): string => (e instanceof Error ? e.message : '');
